import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const CreateTodo = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { caseId } = useParams();
  const [investigators, setInvestigators] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInvestigators();
    if (!caseId) {
      fetchCases();
    }
  }, [caseId]);

  const fetchInvestigators = async () => {
    try {
      const response = await api.get('/users/investigators');
      setInvestigators(response.data.data || []);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await api.get('/cases', { params: { limit: 100 } });
      setCases(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    dueDate: Yup.string().required('Due date is required'),
    priority: Yup.string().required('Priority is required'),
    assignedTo: Yup.string(),
    caseId: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      priority: 'Medium',
      status: 'Pending',
      assignedTo: user?.role === 'Investigator' ? (user.id || user._id) : '',
      dueDate: '',
      caseId: caseId || '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const todoData = {
          title: values.title,
          description: values.description || undefined,
          priority: values.priority,
          status: values.status,
          assignedTo: values.assignedTo || undefined,
          dueDate: values.dueDate,
          caseId: values.caseId || undefined,
        };

        // Remove undefined values
        Object.keys(todoData).forEach(key => {
          if (todoData[key] === undefined) {
            delete todoData[key];
          }
        });

        const response = await api.post('/todos', todoData);
        toast.success('To-Do created successfully');
        
        if (caseId) {
          navigate(`/cases/${caseId}`);
        } else {
          navigate('/todos');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to create to-do');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New To-Do</h1>
          <p className="text-gray-600 mt-1">Create a new task with deadline alerts</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      <form onSubmit={formik.handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter todo title"
          />
          {formik.touched.title && formik.errors.title && (
            <p className="mt-1 text-sm text-red-600">{formik.errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter todo description"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority <span className="text-red-500">*</span>
            </label>
            <select
              name="priority"
              value={formik.values.priority}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
            {formik.touched.priority && formik.errors.priority && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.priority}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Due Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="dueDate"
              value={formik.values.dueDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {formik.touched.dueDate && formik.errors.dueDate && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.dueDate}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign To
            </label>
            {user?.role === 'Investigator' ? (
              <input
                type="text"
                value={`${user.firstName} ${user.lastName} (You)`}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
              />
            ) : (
              <select
                name="assignedTo"
                value={formik.values.assignedTo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select investigator</option>
                {investigators.map((investigator) => (
                  <option key={investigator.id || investigator._id} value={investigator.id || investigator._id}>
                    {investigator.firstName} {investigator.lastName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {!caseId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Case (Optional)
              </label>
              <select
                name="caseId"
                value={formik.values.caseId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">No case</option>
                {cases.map((caseItem) => (
                  <option key={caseItem.id || caseItem._id} value={caseItem.id || caseItem._id}>
                    {caseItem.caseNumber} - {caseItem.title}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create To-Do'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTodo;

